import React from 'react';
import PropTypes from 'prop-types';

const TasksTable = ({
    items,
    subtaskMap,
    label,
    emptyMessage,
    onRowClick,
    onCompleteAction,
    currentUserEmail,
    currentUserTeam,
    isItLeadership,
    hasManagementRights
}) => {
    const getStatusBadge = (status, isOverdue) => {
        if (status === 'completed')
            return (
                <span className="status-badge status-completed">Completed</span>
            );
        if (isOverdue)
            return <span className="status-badge status-overdue">Overdue</span>;
        return <span className="status-badge status-pending">Pending</span>;
    };

    const renderRow = (task, isSubtask = false) => {
        const canComplete =
            onCompleteAction &&
            task.status !== 'completed' &&
            (isItLeadership ||
                task.assignee_email === currentUserEmail ||
                (hasManagementRights && task.team_id === currentUserTeam));

        return (
            <tr
                key={task.id}
                className={`${isSubtask ? 'subtask-row' : ''} ${
                    onRowClick ? 'clickable-row' : ''
                }`}
                onClick={() => onRowClick && onRowClick(task)}
                style={onRowClick ? { cursor: 'pointer' } : {}}>
                <td>{getStatusBadge(task.status, task.sla?.is_overdue)}</td>
                <td>
                    <div
                        className="task-title"
                        style={isSubtask ? { paddingLeft: '1.5rem' } : {}}>
                        {isSubtask && (
                            <span className="subtask-indicator">↳ </span>
                        )}
                        {task.title}
                    </div>
                    {task.notes && (
                        <div
                            className="task-notes"
                            style={isSubtask ? { paddingLeft: '1.5rem' } : {}}>
                            {task.notes}
                        </div>
                    )}
                </td>
                <td>
                    <span className="team-pill">{task.team_id}</span>
                </td>
                <td>
                    {task.assignee_first_name} {task.assignee_last_name}
                </td>
                <td>{task.due_date?.value || task.due_date || '-'}</td>
                <td>
                    {task.sla?.target_hours ? `${task.sla.target_hours}h` : '-'}
                </td>
                {/* Only render Actions column if onCompleteAction is provided */}
                {onCompleteAction !== undefined && (
                    <td onClick={(e) => e.stopPropagation()}>
                        {canComplete && (
                            <button
                                className="btn-complete"
                                onClick={() => onCompleteAction(task)}>
                                ✓ Complete
                            </button>
                        )}
                    </td>
                )}
            </tr>
        );
    };

    return (
        <div className="tasks-table-container mt-4">
            <table className="tasks-table">
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>{label}</th>
                        <th>Team</th>
                        <th>Assignee</th>
                        <th>Due Date</th>
                        <th>SLA</th>
                        {onCompleteAction !== undefined && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {items.map((task) => {
                        let actualParentId = task.task_id;
                        if (
                            !actualParentId &&
                            task.id &&
                            task.id.includes('_')
                        ) {
                            actualParentId = task.id
                                .split('_')
                                .slice(1)
                                .join('_');
                        }
                        const children = subtaskMap.get(actualParentId) || [];
                        return (
                            <React.Fragment key={task.id}>
                                {renderRow(task)}
                                {children.map((sub) => renderRow(sub, true))}
                            </React.Fragment>
                        );
                    })}
                    {items.length === 0 && (
                        <tr>
                            <td
                                colSpan={
                                    onCompleteAction !== undefined ? '7' : '6'
                                }
                                style={{
                                    textAlign: 'center',
                                    padding: '2rem'
                                }}>
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

TasksTable.propTypes = {
    items: PropTypes.array.isRequired,
    subtaskMap: PropTypes.instanceOf(Map).isRequired,
    label: PropTypes.string.isRequired,
    emptyMessage: PropTypes.node.isRequired,
    onRowClick: PropTypes.func,
    onCompleteAction: PropTypes.func,
    currentUserEmail: PropTypes.string,
    currentUserTeam: PropTypes.string,
    isItLeadership: PropTypes.bool,
    hasManagementRights: PropTypes.bool
};

export default TasksTable;
